import { ptzaModulesManager } from './ui/modules/modules-manager.js';
import ptzaRender, { PTZA_COMPONENTS } from '../src/ui/extras/renderApp.js';
import { ptzaConfig } from './controllers/config.js';
import { configBindings } from './ui/extras/configBindings.js';
import { RenderBase } from './ui/extras/render-base.js';
import { ptzaAccess } from './controllers/access.js';

const renderFacade = Object.freeze({
    builder: (...args) => ptzaRender.builder(...args)
});

const accessFacade = Object.freeze({
    isUnlocked: (...args) => ptzaAccess.isUnlocked(...args),
    unlock: (...args) => ptzaAccess.unlock(...args),
    lock: (...args) => ptzaAccess.lock(...args)
});

const formsFacade = Object.freeze({
    async submit(formKey, fields) {
        /*
         * Forms es infraestructura global del Core, no un módulo de página.
         * La carga real queda diferida hasta el primer submit para que las
         * páginas que no utilizan formularios no incorporen ese servicio al
         * flujo inicial.
         *
         * No se importa FormsService antes de que config.json haya sido
         * cargado: ApiService necesita esa configuración al construirse.
         */
        if (!ptzaConfig.isLoadConfig()) {
            return {
                ok: false,
                queued: false,
                jobId: null,
                formKey: String(formKey ?? '').trim(),
                code: 'CORE_NOT_READY',
                uncertain: false,
                cancelled: false
            };
        }

        const { formsService } = await import(
            './controllers/services/formsService.js'
        );

        return await formsService.submit(formKey, fields);
    }
});

window.PTZA = {
    config: null,
    modules: null,
    render: renderFacade,
    access: accessFacade,
    forms: formsFacade
};

/*
 * ETAPA 2 / ITERACIÓN 6 - REITERACIÓN 2
 *
 * id_carrito se captura de forma sincrónica apenas se evalúa el Core, antes de
 * DOMContentLoaded, initRender(), initConfig() o cualquier inicialización de
 * módulos. De esta manera ninguna operación posterior puede hacer que el Core
 * pierda la intención de recupero original de la URL.
 *
 * Es estado privado del módulo app.js; no se publica ninguna variable nueva en
 * window.
 */
const initialUrl = new URL(window.location.href);
const requestedCarritoId = String(
    initialUrl.searchParams.get('id_carrito') ?? ''
).trim();

document.addEventListener('DOMContentLoaded', async () => {
    if (!await initRender()) return false;

    if (!await initConfig()) return false;

    // Aplica bindings declarativos sobre el HTML ya montado por Render.
    // Ejemplos: data-ptza-text="clientInfo.nombreCliente" y
    // data-ptza-href="clientInfo.urlCliente".
    configBindings.apply(document);

    // Si la navegación inicial pidió un recupero, su intento debe terminar antes
    // de permitir que cualquier módulo cargue o cree un carrito normal.
    if (requestedCarritoId) {
        await recoverCarritoFromUrl(requestedCarritoId);
    }

    await initModules();
});

async function initConfig(){
    window.PTZA.config = ptzaConfig;
    return await window.PTZA.config.loadConfig();
}

async function recoverCarritoFromUrl(idCarrito){
    const requestedId = String(idCarrito ?? '').trim();

    if (!requestedId) {
        return false;
    }

    // El recupero pertenece exclusivamente a SITE Nest.
    if (ptzaConfig.getModo() !== 'api') {
        return false;
    }

    const { apiService } = await import(
        './controllers/services/apiService.js'
    );
    const { storageService } = await import(
        './controllers/services/storageService.js'
    );

    let response = await apiService.sitePost(
        `/site/carrito/${encodeURIComponent(requestedId)}/recuperar`
    );

    /*
     * Si el POST tuvo un resultado de red incierto no se repite la mutación.
     * Si Nest alcanzó a reasociar el carrito, el GET normal ya podrá leerlo con
     * la sesión SITE actual y confirma el estado final sin duplicar el recupero.
     */
    if (response?.code === 'MUTATION_RESULT_UNKNOWN') {
        response = await apiService.siteGet(
            `/site/carrito/${encodeURIComponent(requestedId)}`
        );
    }

    if (!response?.ok || !response.data) {
        console.warn('Propuestaza: no se pudo recuperar el carrito solicitado.');
        return false;
    }

    const idCarritoRespuesta = String(
        response.data?.idCarrito ?? ''
    ).trim();

    if (!idCarritoRespuesta || idCarritoRespuesta !== requestedId) {
        console.warn('Propuestaza: respuesta de recupero de carrito inválida.');
        return false;
    }

    /*
     * Nest ya aplicó todas las reglas del recupero. El Core guarda CarritoDto
     * como snapshot API antes de que CartSource exista. Cuando initModules()
     * inicie el carrito, loadCarrito() leerá este UUID y ya no creará uno nuevo.
     */
    if (!storageService.setDataCarrito(response.data, 'api')) {
        console.warn('Propuestaza: no se pudo guardar el carrito recuperado.');
        return false;
    }

    const urlNavegador = new URL(window.location.href);
    urlNavegador.searchParams.delete('id_carrito');
    window.history.replaceState(
        {},
        '',
        urlNavegador.pathname + urlNavegador.search + urlNavegador.hash
    );

    console.log('Propuestaza: carrito recuperado.');
    return true;
}

async function initModules(){
    await ptzaModulesManager.init();
}

async function initRender() {
    if (!ptzaRender) {
        console.error('[PTZA Core] No se pudo cargar ptzaRender.');
        return false;
    }

    if (typeof ptzaRender.mountAll !== 'function') {
        console.error('[PTZA Core] ptzaRender.mountAll no está disponible.');
        return false;
    }

    if (typeof ptzaRender.builder !== 'function') {
        console.error('[PTZA Core] ptzaRender.builder no está disponible.');
        return false;
    }

    if (!PTZA_COMPONENTS || typeof PTZA_COMPONENTS !== 'object') {
        console.error('[PTZA Core] PTZA_COMPONENTS no está disponible.');
        return false;
    }

    /*
     * RenderBase dejó de formar parte del Core. Builder se carga únicamente
     * durante la inicialización de Render y su prototipo se conecta a la clase
     * puente que todavía utilizan las vistas existentes mediante `extends
     * RenderBase`.
     *
     * La implementación actual de RenderBase no mantiene estado propio en su
     * constructor, por lo que las vistas pueden conservar su estructura actual
     * mientras los métodos viven en ptzaRender/builder.
     */
    const builder = await ptzaRender.builder();
    const builderPrototype = builder
        ? Object.getPrototypeOf(builder)
        : null;

    if (!builderPrototype || builderPrototype === Object.prototype) {
        console.error('[PTZA Core] No se pudo inicializar ptzaRender.builder.');
        return false;
    }

    Object.setPrototypeOf(
        RenderBase.prototype,
        builderPrototype
    );

    await ptzaRender.mountAll(document);
    return true;
}
